<template>
  <div class="component-upload-image">
    <el-upload :multiple="multiple" :action="''" list-type="picture" :auto-upload="true"
      :before-upload="handleBeforeUpload" :limit="limit" :directory="true" :on-error="handleUploadError"
      :on-exceed="handleExceed" ref="imageUpload" :before-remove="handleDelete" :show-file-list="false"
      :headers="headers" :file-list="fileList" :on-preview="handlePictureCardPreview"
      :class="{ hide: fileList.length >= limit }">
      <!-- <el-icon class="avatar-uploader-icon"><plus /></el-icon> -->
      <el-button type="primary">点击上传</el-button>

      <!-- 自定义文件项内容 -->

    </el-upload>

    <!-- 拖拽排序区域 -->
    <div class="sortable-container" v-if="fileList.length > 0">
      <draggable v-model="fileList" item-key="name" @end="handleDragEnd" class="sortable-list">
        <template #item="{ element, index }">
          <el-badge :value="index + 1" color="green">
            <div class="sortable-item">
              <div class="sortable-item-thumbnail" v-if="isImage(element.fileType || getFileExtension(element.name))">
                <img :src="element.url" alt="" />
              </div>
              <div class="sortable-item-thumbnail video-thumbnail"
                v-else-if="isVideo(element.fileType || getFileExtension(element.name))">
                <img :src="`${element.url}?x-oss-process=video/snapshot,t_0,f_jpg,w_800,h_600,m_fast`" alt="" />
              </div>
              <div class="sortable-item-actions">
                <!-- <el-icon class="drag-handle"><Rank /></el-icon> -->
                <el-icon class="drag-handle" @click="handlePictureCardPreview(element)">
                  <ZoomIn v-if="isImage(element.fileType || getFileExtension(element.name))" />
                  <VideoPlay v-else-if="isVideo(element.fileType || getFileExtension(element.name))" />
                </el-icon>
                <el-icon v-if="showEditButton" class="drag-handle" @click="handleEdit(element)">
                  <Edit />
                </el-icon>
                <el-icon v-if="!hideReuploadButton" class="drag-handle" @click="handleReupload(element)">
                  <Upload />
                </el-icon>
                <el-icon class="drag-handle" @click="handleDelete(element)">
                  <Delete />
                </el-icon>
              </div>
              <p class="sortable-item-extra">
                {{ element.name?.includes('https') ? element.name.split('/')[element.name.split('/').length - 1] :
                  element.name }}
              </p>
            </div>
          </el-badge>
        </template>
      </draggable>
    </div>

    <el-space>
      <!-- 上传提示 -->
      <div class="el-upload__tip" v-if="showTip">
        请上传
        <template v-if="fileSize || videoFileSize">
          <span v-if="fileType.includes('png')">图片大小不超过 <b style="color: #f56c6c">{{ fileSize }}MB</b>，</span>
          <span v-if="fileType.includes('mp4')">视频大小不超过 <b style="color: #f56c6c">{{ videoFileSize }}MB</b></span>
        </template>
        <template v-if="fileType">
          格式为 <b style="color: #f56c6c">{{ fileType.join("/") }}</b>
        </template>
        的文件
      </div>
      <div class="el-upload__tip" v-if="customTip">
        {{ customTip }}
      </div>
    </el-space>

    <el-dialog v-model="dialogVisible" title="预览" width="600px" append-to-body>
      <img v-if="isImage(dialogFileType)" :src="dialogImageUrl"
        style="display: block; max-width: 300px; margin: 0 auto" />
      <video v-else-if="isVideo(dialogFileType)" :src="dialogImageUrl" controls
        style="display: block; max-width: 300px; margin: 0 auto"></video>
    </el-dialog>

    <!-- 编辑弹窗 -->
    <el-dialog v-model="editDialogVisible" title="编辑" width="500px" append-to-body>
      <div class="edit-dialog-content">
        <div class="edit-image-container" 
          @mouseenter="showReuploadOverlay = true" @mouseleave="showReuploadOverlay = false">
          <img v-if="isImage(editFileType)" :src="editImageUrl" />
          <video v-else-if="isVideo(editFileType)" :src="editImageUrl" controls></video>
          <!-- 重新上传悬浮层 -->
          <div v-if="showReuploadOverlay" class="reupload-overlay" @click="handleEditReupload">
            <el-icon class="reupload-icon">
              <Upload />
            </el-icon>
            <span class="reupload-text">重新上传</span>
          </div>
        </div>
      </div>
      <el-form :model="editForm" label-position="top">
        <el-form-item label="课件内容AI功能配置模版">
          <el-input v-model="editForm.content" type="textarea" :autosize="{ minRows: 4, maxRows: 8 }"
            placeholder="请输入内容配置，建议JSON格式" />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="editDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="saveEdit">确 定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import OSS from "ali-oss";
import { getToken } from "@/utils/auth";
import { isExternal } from "@/utils/validate";
import { getAliyun } from "@/api/common";
import { Delete, ZoomIn, Plus, VideoPlay, Upload, Rank, Edit } from '@element-plus/icons-vue';
import draggable from 'vuedraggable';
import dayjs from 'dayjs'

const props = defineProps({
  modelValue: [String, Object, Array],
  // 图片数量限制
  limit: {
    type: Number,
    default: 5,
  },
  // 图片大小限制(MB)
  fileSize: {
    type: Number,
    default: 5,
  },
  //  是否多选
  multiple: {
    type: Boolean,
    default: false,
  },
  // 视频大小限制(MB)
  videoFileSize: {
    type: Number,
    default: 50,
  },
  // 文件类型, 例如['png', 'jpg', 'jpeg', 'mp4', 'mov']
  fileType: {
    type: Array,
    default: () => ["png", "jpg"],
  },
  // 是否显示提示
  isShowTip: {
    type: Boolean,
    default: true,
  },
  customTip: {
    type: String,
    default: "",
  },
  fileInfoLimit: {
    type: Object,
    default: () => ({
      width: 100,
      height: 100,
      isJudge: false
    }),
  },
  // 是否显示编辑按钮
  showEditButton: {
    type: Boolean,
    default: false,
  },
  // 是否隐藏重新上传按钮
  hideReuploadButton: {
    type: Boolean,
    default: false,
  }
});

const { proxy } = getCurrentInstance();
const emit = defineEmits();
const number = ref(0);
const autoSort = ref(false);
const uploadList = ref([]);
const dialogImageUrl = ref("");
const durationMillSeconds = ref(0)
const dialogVisible = ref(false);
const dialogFileType = ref(""); // 新增：记录当前预览文件的类型
const baseUrl = import.meta.env.VITE_APP_BASE_API;
const uploadImgUrl = ref(import.meta.env.VITE_APP_BASE_API + "/common/upload"); // 上传的图片服务器地址
const headers = ref({ Authorization: "Bearer " + getToken() });
const fileList = ref([]);
const ossClient = ref(null);
const preUrl = ref("");
const cdnUrl = ref("");
// 编辑弹窗相关
const editDialogVisible = ref(false);
const editImageUrl = ref("");
const editFileType = ref("");
const currentEditFile = ref(null);
const showReuploadOverlay = ref(false);
const editForm = ref({
  content: ""
});
const showTip = computed(
  () => props.isShowTip && (props.fileType || props.fileSize || props.videoFileSize)
);


// 判断是否为图片
const isImage = (fileType) => {
  const imageTypes = ["png", "jpg", "gif", "bmp"];
  return imageTypes.some(type => fileType?.toLowerCase().includes(type));
};

// 判断是否为视频
const isVideo = (fileType) => {
  const videoTypes = ["mp4", "mov", "avi", "wmv", "flv"];
  return videoTypes.some(type => fileType?.toLowerCase().includes(type));
};

// 获取文件扩展名
const getFileExtension = (fileName) => {
  if (fileName && fileName.lastIndexOf(".") > -1) {
    return fileName.slice(fileName.lastIndexOf(".") + 1).toLowerCase();
  }
  return "";
};


watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      let list = [];

      // 尝试解析JSON格式的文件数据
      try {
        if (typeof val === 'string' && val.startsWith('[')) {
          // JSON格式的文件数据
          const jsonData = JSON.parse(val);
          list = jsonData.map((item) => ({
            name: item.name || item.url,
            url: item.url.indexOf(baseUrl) === -1 && !isExternal(item.url) ? baseUrl + item.url : item.url,
            fileType: item.fileType || getFileExtension(item.name || item.url),
            aiConfig: item.aiConfig || ""
          }));
        } else {
          // 原有的字符串格式
          list = Array.isArray(val) ? val : val.split(",");
          list = list.map((item) => {
            if (typeof item === "string") {
              if (item.indexOf(baseUrl) === -1 && !isExternal(item)) {
                item = { name: item, url: baseUrl + item };
              } else {
                item = { name: item, url: item };
              }
            }
            // 添加文件类型属性
            if (item.name) {
              const extension = getFileExtension(item.name);
              item.fileType = extension;
            }
            return item;
          });
        }
      } catch (error) {
        // 如果JSON解析失败，使用原有的字符串处理方式
        list = Array.isArray(val) ? val : val.split(",");
        list = list.map((item) => {
          if (typeof item === "string") {
            if (item.indexOf(baseUrl) === -1 && !isExternal(item)) {
              item = { name: item, url: baseUrl + item };
            } else {
              item = { name: item, url: item };
            }
          }
          // 添加文件类型属性
          if (item.name) {
            const extension = getFileExtension(item.name);
            item.fileType = extension;
          }
          return item;
        });
      }

      fileList.value = list;
    } else {
      fileList.value = [];
      return [];
    }
  },
  { deep: true, immediate: true }
);

const getImageFileInfo = (source) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const data = e.target.result;
    // 加载图片获取图片真实宽度和高度
    const image = new Image();
    image.onload = () => {
      const { width } = image;
      const { height } = image;
      resolve({
        width,
        height,
      });
    };
    image.src = data;
  };
  reader.readAsDataURL(source);
});

// 上传前loading加载
const handleBeforeUpload = async (file) => {
  await initOssClient();
  let isValidFile = false;
  let fileExtension = getFileExtension(file.name);
  const isVideoFile = isVideo(fileExtension);
  const isImageFile = isImage(fileExtension);

  if (props.fileType.length) {
    isValidFile = props.fileType.some((type) => {
      if (file.type.indexOf(type) > -1) return true;
      if (fileExtension && fileExtension.indexOf(type) > -1) return true;
      return false;
    });
  } else {
    isValidFile = file.type.indexOf("image") > -1 || file.type.indexOf("video") > -1;
  }

  if (!isValidFile) {
    proxy.$modal.msgError(
      `文件格式不正确，请上传${props.fileType.join("/")}格式文件!`
    );
    return false;
  }

  if (file.name.includes(",")) {
    proxy.$modal.msgError("文件名不正确，不能包含英文逗号!");
    return false;
  }
  if (props.fileInfoLimit.isJudge && isImageFile) {
    const imageInfo = await getImageFileInfo(file);
    if (imageInfo.width !== props.fileInfoLimit.width || imageInfo.height !== props.fileInfoLimit.height) {
      proxy.$modal.msgError(`图片宽高尺寸应该为： ${props.fileInfoLimit.width} * ${props.fileInfoLimit.height}`);
      return false;
    }
  }

  // 根据文件类型应用不同的大小限制
  const sizeLimit = isVideoFile ? props.videoFileSize : props.fileSize;
  const fileSize = file.size / 1024 / 1024;
  // const fileInfo = await getImageFileInfo(file);
  // console.log('fileInfo', file, fileInfo)
  if (sizeLimit && fileSize > sizeLimit) {
    proxy.$modal.msgError(
      `上传${isVideoFile ? '视频' : '图片'}大小不能超过 ${sizeLimit} MB!`
    );
    return false;
  }

  proxy.$modal.loading(`正在上传${isVideoFile ? '视频' : '图片'}，请稍候...`);
  number.value++;
  console.log('file', file, number)
  try {
    // 生成OSS文件路径
    const fileName =
      preUrl.value + "/" + new Date().getTime() + "/" + file.name;
    // 执行上传
    const result = await ossClient?.value.multipartUpload(fileName, file);
    // 手动触发成功回调
    handleUploadSuccess(
      {
        code: 200,
        url: cdnUrl.value + "/" + result.name,
        fileName: cdnUrl.value + "/" + result.name,
        fileType: fileExtension,
        isVideo: isVideoFile,
        sortValue: file.name
      },
      file
    );

    return false; // 阻止 el-upload 默认上传
  } catch (error) {
    proxy.$modal.msgError("上传失败");
    proxy.$modal.closeLoading();
    return false;
  }
};

// 文件个数超出
function handleExceed() {
  proxy.$modal.msgError(`上传文件数量不能超过 ${props.limit} 个!`);
}

// 上传成功回调
function handleUploadSuccess(res, file) {
  if (res.code === 200) {
    const fileExtension = getFileExtension(file.name);

    uploadList.value.push({
      name: res.fileName,
      url: res.url,
      fileType: fileExtension,
      sortValue: res.sortValue
    });

    uploadedSuccessfully();
  } else {
    number.value--;
    proxy.$modal.closeLoading();
    proxy.$modal.msgError(res.msg);
    proxy.$refs.imageUpload.handleRemove(file);
    uploadedSuccessfully();
  }
}

// 删除图片
function handleDelete(file) {
  const findex = fileList.value.map((f) => f.name).indexOf(file.name);
  if (findex > -1 && uploadList.value.length === number.value) {
    fileList.value.splice(findex, 1);
    emit("update:modelValue", listToString(fileList.value));
    return false;
  }
}

// 上传结束处理
function uploadedSuccessfully() {
  console.log(121, number.value, uploadList.value)
  if (number.value > 0 && uploadList.value.length === number.value) {
    const sortList = uploadList.value.sort((a, b) => a.sortValue.localeCompare(b.sortValue))
    fileList.value = fileList.value
      .filter((f) => f.url !== undefined)
      .concat(sortList)
    uploadList.value = [];
    number.value = 0;
    emit("update:modelValue", listToString(fileList.value));
    proxy.$modal.closeLoading();
  }
}

// 上传失败
function handleUploadError() {
  proxy.$modal.msgError("上传文件失败");
  proxy.$modal.closeLoading();
}

// 预览
function handlePictureCardPreview(file) {
  dialogImageUrl.value = file.url;
  dialogFileType.value = file.fileType || getFileExtension(file.name);
  dialogVisible.value = true;
}

// 对象转成指定字符串分隔
function listToString(list, separator) {
  // console.log('list', list)
  // 如果列表中有文件包含AI配置，返回JSON字符串
  const hasAiConfig = list.some(item => item.aiConfig);

  if (hasAiConfig) {
    // 返回完整的文件信息数组的JSON字符串
    const fileData = list.map(item => ({
      url: item.url ? item.url.replace(baseUrl, "") : "",
      name: item.name,
      fileType: item.fileType,
      aiConfig: item.aiConfig || ""
    })).filter(item => item.url && item.url.indexOf("blob:") !== 0);

    return JSON.stringify(fileData);
  } else {
    // 保持原有的字符串格式以保证向后兼容
    let strs = "";
    separator = separator || ",";
    for (let i in list) {
      if (undefined !== list[i].url && list[i].url.indexOf("blob:") !== 0) {
        strs += list[i].url.replace(baseUrl, "") + separator;
      }
    }
    return strs != "" ? strs.substr(0, strs.length - 1) : "";
  }
}
// 获取OSS配置信息
const getOssPolicy = async () => {
  const res = await getAliyun();
  return res.data;
};

// 初始化OSS客户端
const initOssClient = async () => {
  if (durationMillSeconds.value && dayjs(durationMillSeconds.value).diff(dayjs(), 'minute') >= 5) {
    return
  }
  const policy = await getOssPolicy();
  durationMillSeconds.value = policy?.durationMillSeconds || dayjs().add(60, 'minute').valueOf();
  preUrl.value = policy?.preUrl;
  cdnUrl.value = policy?.cdnUrl;
  ossClient.value = new OSS({
    region: 'oss-accelerate',
    accessKeyId: policy?.credentials?.accessKeyId,
    accessKeySecret: policy?.credentials?.accessKeySecret,
    stsToken: policy?.credentials?.securityToken,
    bucket: policy?.bucketName,
    refreshSTSToken: async () => {
      // 调用后端接口获取新的STS Token
      const res = await getAliyun();
      return {
        accessKeyId: res?.data?.credentials?.accessKeyId,
        accessKeySecret: res?.data?.credentials?.accessKeySecret,
        stsToken: res?.data?.credentials?.securityToken,
      };
    },
    refreshSTSTokenInterval: 30 * 60 * 1000, // 30分钟刷新一次
  });
};
initOssClient();

// 处理拖拽结束
const handleDragEnd = () => {
  emit("update:modelValue", listToString(fileList.value));
};

// 处理重新上传
const handleReupload = async (file) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = props.fileType.join(',');
  input.onchange = async (e) => {
    const newFile = e.target.files[0];
    if (newFile) {
      const fileExtension = getFileExtension(newFile.name);
      const isVideoFile = isVideo(fileExtension);

      // 检查文件类型
      if (!props.fileType.some(type => fileExtension?.toLowerCase().includes(type))) {
        proxy.$modal.msgError(`文件格式不正确，请上传${props.fileType.join("/")}格式文件!`);
        return;
      }

      // 检查文件大小
      const sizeLimit = isVideoFile ? props.videoFileSize : props.fileSize;
      const fileSize = newFile.size / 1024 / 1024;
      if (sizeLimit && fileSize > sizeLimit) {
        proxy.$modal.msgError(`上传${isVideoFile ? '视频' : '图片'}大小不能超过 ${sizeLimit} MB!`);
        return;
      }

      proxy.$modal.loading(`正在上传${isVideoFile ? '视频' : '图片'}，请稍候...`);

      try {
        await initOssClient()
        // 生成OSS文件路径
        const fileName = preUrl.value + "/" + new Date().getTime() + "/" + newFile.name;
        // 执行上传
        const result = await ossClient?.value.put(fileName, newFile);

        // 更新文件列表
        const index = fileList.value.findIndex(f => f.name === file.name);
        if (index !== -1) {
          fileList.value[index] = {
            name: cdnUrl.value + "/" + result.name,
            url: cdnUrl.value + "/" + result.name,
            fileType: fileExtension
          };
          emit("update:modelValue", listToString(fileList.value));
        }

        proxy.$modal.closeLoading();
      } catch (error) {
        proxy.$modal.msgError("上传失败");
        proxy.$modal.closeLoading();
      }
    }
  };
  input.click();
};

// 处理编辑
const handleEdit = (file) => {
  currentEditFile.value = file;
  editImageUrl.value = file.url;
  editFileType.value = file.fileType || getFileExtension(file.name);
  editForm.value.content = file.aiConfig || "";
  editDialogVisible.value = true;
};

// 保存编辑
const saveEdit = () => {
  if (currentEditFile.value) {
    // 更新文件对象的AI配置
    const index = fileList.value.findIndex(f => f.name === currentEditFile.value.name);
    if (index !== -1) {
      fileList.value[index].aiConfig = editForm.value.content;
      console.log("🚀 ~ saveEdit ~ fileList.value[index].aiConfig:", fileList.value[index].aiConfig)
      emit("update:modelValue", listToString(fileList.value));
    }
  }
  editDialogVisible.value = false;
  proxy.$modal.msgSuccess("编辑保存成功");
};

// 编辑弹窗中的重新上传
const handleEditReupload = async () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = props.fileType.join(',');
  input.onchange = async (e) => {
    const newFile = e.target.files[0];
    if (newFile) {
      const fileExtension = getFileExtension(newFile.name);
      const isVideoFile = isVideo(fileExtension);

      // 检查文件类型
      if (!props.fileType.some(type => fileExtension?.toLowerCase().includes(type))) {
        proxy.$modal.msgError(`文件格式不正确，请上传${props.fileType.join("/")}格式文件!`);
        return;
      }

      // 检查文件大小
      const sizeLimit = isVideoFile ? props.videoFileSize : props.fileSize;
      const fileSize = newFile.size / 1024 / 1024;
      if (sizeLimit && fileSize > sizeLimit) {
        proxy.$modal.msgError(`上传${isVideoFile ? '视频' : '图片'}大小不能超过 ${sizeLimit} MB!`);
        return;
      }

      proxy.$modal.loading(`正在上传${isVideoFile ? '视频' : '图片'}，请稍候...`);

      try {
        await initOssClient()
        // 生成OSS文件路径
        const fileName = preUrl.value + "/" + new Date().getTime() + "/" + newFile.name;
        // 执行上传
        const result = await ossClient?.value.put(fileName, newFile);

        // 更新当前编辑文件和文件列表
        const newUrl = cdnUrl.value + "/" + result.name;
        editImageUrl.value = newUrl;
        editFileType.value = fileExtension;

        const index = fileList.value.findIndex(f => f.name === currentEditFile.value.name);
        if (index !== -1) {
          fileList.value[index] = {
            ...fileList.value[index],
            name: newUrl,
            url: newUrl,
            fileType: fileExtension
          };
          currentEditFile.value = fileList.value[index];
          emit("update:modelValue", listToString(fileList.value));
        }

        proxy.$modal.closeLoading();
        proxy.$modal.msgSuccess("重新上传成功");
      } catch (error) {
        proxy.$modal.msgError("上传失败");
        proxy.$modal.closeLoading();
      }
    }
  };
  input.click();
};
</script>

<style scoped lang="scss">
// .el-upload--picture-card 控制加号部分
:deep(.hide .el-upload--picture-card) {
  display: none;
}

// 视频预览相关样式
:deep(.el-dialog__body) {
  text-align: center;

  video {
    max-width: 100%;
    max-height: 70vh;
  }
}

// 视频缩略图样式
:deep(.video-thumbnail) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #2c3e50;

  .video-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.2);

    .video-icon {
      font-size: 24px;
      color: #fff;
    }
  }
}

:deep(.el-upload-list--picture-card .el-upload-list__item-thumbnail) {
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.sortable-container {
  margin-top: 20px;
}

.sortable-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.sortable-item {
  position: relative;
  width: 148px;
  height: 177px;
  border: 1px solid #d9d9d9;
  box-sizing: border-box;
  border-radius: 6px;

  // overflow: hidden;
  // cursor: move;
  &:hover {
    border-color: #409eff;

    .sortable-item-actions {
      opacity: 1;
    }
  }
}

.sortable-item-thumbnail {
  width: 148px;
  height: 148px;

  img {
    width: 148px;
    height: 148px;
    object-fit: cover;
  }
}

.sortable-item-actions {
  position: absolute;
  top: 0;
  left: 0;
  width: 148px;
  height: 148px;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;

  .drag-handle {
    color: #fff;
    font-size: 20px;

    &:nth-child(even) {
      margin-left: 10px;
      margin-right: 10px;
    }
  }
}

.sortable-item-extra {
  width: 100%;
  height: 25px;
  text-align: center;
  line-height: 25px;
  margin: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis
}

:deep(.el-upload-list__item-actions) {
  .el-upload-list__item-reupload {
    margin: 0 5px;
    cursor: pointer;

    &:hover {
      color: #409eff;
    }
  }
}

// 编辑弹窗内容样式
.edit-dialog-content {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.edit-image-container {
  position: relative;
  display: inline-block;
  
  img, video {
    max-width: 200px;
    max-height: 200px;
    display: block;
    border-radius: 6px;
  }
}

// 编辑弹窗重新上传悬浮层样式
.reupload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
  
  .reupload-icon {
    color: #fff;
    font-size: 20px;
    margin-bottom: 6px;
    transition: transform 0.2s ease;
  }
  
  .reupload-text {
    color: #fff;
    font-size: 12px;
    font-weight: 500;
  }
  
  &:hover {
    background: rgba(0, 0, 0, 0.6);
    
    .reupload-icon {
      transform: scale(1.1);
    }
  }
}
</style>
